import React, { useContext, useMemo, useState } from "react";
import ResumeUpload from "../Resume";
import SvgButton, { SVG_ICON_SURFACE_CLASS } from "components/base/SvgButton";
import { APPLICATION_TAB_NAMES, JOB_APPLICATION_MATCH_STATUS } from "lookup";
import DropDownMenu from "components/DropDownMenu";
import { AuthContext, MatchContext } from "context/providers";
import UserInfoModal from "containers/ModalContainer/modals/UserInfoModal";
import { isMatchPreShortlistedStatusType } from "utils/helpers/match";
import { getUserName } from "utils/helpers/users";
import { PerfectFitJobModal } from "containers/ModalContainer/modals";
import { CTA_TITLES } from "components/UserCardList/constants/ctaTitles";
import { useConfirmationEmailNotification } from "components/UserCardList/hooks/useConfirmationEmailNotification";
import { AtSign, Copy, Phone } from "lucide-react";
import { copyToClipboard } from "utils/copyToClipboard";
import { CtaVerticalDivider } from "../../../shared/CtaPrimitives";

const UserActionIcons = ({
  hit,
  collectionKey,
  activeTabName,
  updateHit,
  showModal,
  showResume = true,
}) => {
  const [perfectFitJobModalIsOpen, setPerfectFitJobModalIsOpen] =
    useState(false);
  const [userInfoModalIsOpen, setUserInfoModalIsOpen] = useState(false);

  const { user } = useContext(AuthContext);
  const {
    confirmationEmailTooltip,
    isSendingConfirmationEmail,
    sendUserNotification,
  } = useConfirmationEmailNotification(hit.email);

  const isAdmin = useMemo(
    () =>
      user?.attributes?.groups?.includes(
        process.env.REACT_APP_COGNITO_ADMIN_GROUP
      ),
    [user]
  );

  const { updateMatchUserFieldLocally, matches } = useContext(MatchContext);

  const saveAttributes = async (attributes) => {
    const attributesToUpdate = {
      input: {
        id: hit.id,
        ...attributes,
      },
    };
    try {
      await updateHit(attributesToUpdate);

      if (matches.length > 0) {
        const applicationId =
          hit.match?.applicationId ||
          matches.find(({ userId }) => userId === hit.id)?.applicationId;

        if (!applicationId) {
          return;
        }

        updateMatchUserFieldLocally(
          applicationId,
          "resumeLocation",
          attributes.resumeLocation
        );
      }
    } catch (error) {
      console.log("Error when updating profile info", error);
    }
  };

  const CustomLink = ({ url, label, title }) => (
    <a target="_blank" rel="noreferrer" href={url} title={title}>
      <span className="w-full text-center">{label}</span>
    </a>
  );

  const CopyIconButton = ({ value, label, title, children }) => (
    <button
      type="button"
      className={SVG_ICON_SURFACE_CLASS}
      title={title}
      aria-label={title}
      onClick={() => copyToClipboard(value, label)}
    >
      <span className="relative flex h-full w-full items-center justify-center">
        {children}
        <Copy className="absolute -bottom-1 -right-1 h-3 w-3 rounded-full bg-white text-gray-500" />
      </span>
    </button>
  );

  const menuOptions = [
    collectionKey === "match" &&
      !isMatchPreShortlistedStatusType(hit.match?.status) &&
      hit.match?.status !== JOB_APPLICATION_MATCH_STATUS.SHORTLISTED &&
      hit.match?.status !== JOB_APPLICATION_MATCH_STATUS.SKIPPED && (
        <p
          title={CTA_TITLES.candidateQuestions}
          onClick={() =>
            showModal({
              type: "match",
              applicationId: hit.match?.applicationId,
              jobOpportunityId: hit.match?.jobOpportunityId,
              freelancerPitchPrefill: hit.match?.freelancerPitchPrefill,
              hitStatus: hit.match?.status,
              freelancerPitch: hit.match?.freelancerPitch,
              isUpdate: true,
            })
          }
        >
          Candidate Questions
        </p>
      ),
    <p
      onClick={() =>
        showModal({
          type: "link",
          userId: hit.id,
          applicationId: hit?.match?.applicationId,
          collectionKey,
        })
      }
      title={CTA_TITLES.addLink}
    >
      Add Link
    </p>,
    isAdmin && getUserName(hit) && (
      <CustomLink
        url={`https://admin.torc.dev/users/${getUserName(hit)}`}
        label={"Admin Tool"}
        title={CTA_TITLES.adminTool}
      />
    ),
    <CustomLink
      url={`${process.env.REACT_APP_SFDC_BASE_URL}/torcUserRedirect?id=${hit.id}`}
      label={"SFDC User"}
      title={CTA_TITLES.sfdcUser}
    />,
    activeTabName === APPLICATION_TAB_NAMES.MATCHED && (
      <CustomLink
        url={`${process.env.REACT_APP_SFDC_BASE_URL}/customFlowOperation?platformmatchid=${hit?.match?.id}`}
        label={"SFDC Match"}
        title={CTA_TITLES.sfdcMatch}
      />
    ),
  ];

  return (
    <div
      id="talent-card-actions"
      className="flex items-center gap-x  md:gap-x-2"
    >
      <SvgButton
        icon="edit"
        tooltip={CTA_TITLES.editProfile}
        onClick={() => showModal({ type: "userEditProfile", userId: hit.id })}
      />

      {hit.email ? (
        <CopyIconButton
          value={hit.email}
          label="Email"
          title={CTA_TITLES.copyEmail}
        >
          <AtSign className="h-full w-full" />
        </CopyIconButton>
      ) : null}

      {hit?.phone?.number ? (
        <CopyIconButton
          value={hit.phone.number}
          label="Phone number"
          title={CTA_TITLES.copyPhone}
        >
          <Phone className="h-full w-full" />
        </CopyIconButton>
      ) : null}

      {hit?.phone?.whatsAppAllowed ? (
        <a
          className="flex justify-center text-sm transform transition duration-500 hover:scale-110"
          href={`whatsapp://send?phone=${hit.phone.number}`}
          target="_blank"
          rel="noreferrer"
          title={CTA_TITLES.whatsApp}
        >
          <SvgButton icon="whatsapp" tooltip={CTA_TITLES.whatsApp} />
        </a>
      ) : null}
      {user?.canSendAssessments ? (
        <SvgButton
          icon="openBook"
          tooltip={CTA_TITLES.sendAssessment}
          onClick={() => showModal({ type: "assessment", userId: hit.id })}
        />
      ) : null}
      {hit.isUnclaimed ? (
        <SvgButton
          icon="simpleEmail"
          tooltip={confirmationEmailTooltip}
          onClick={sendUserNotification}
          isLoading={isSendingConfirmationEmail}
          disabled={isSendingConfirmationEmail}
        />
      ) : null}
      {showResume ? <ResumeUpload user={hit} save={saveAttributes} /> : null}
      <div title={CTA_TITLES.moreInfo}>
        <SvgButton
          icon="info"
          tooltip={CTA_TITLES.moreInfo}
          onClick={() => setUserInfoModalIsOpen(true)}
        />
      </div>
      <UserInfoModal
        open={userInfoModalIsOpen}
        onOpenChange={setUserInfoModalIsOpen}
        match={hit.match || {}}
        user={hit}
      />

      <SvgButton
        icon="compass"
        tooltip={CTA_TITLES.findPerfectFitJobs}
        onClick={() => setPerfectFitJobModalIsOpen(true)}
      />
      <PerfectFitJobModal
        isDialogOpen={perfectFitJobModalIsOpen}
        setDialogVisibility={setPerfectFitJobModalIsOpen}
        user={hit}
      />
      <CtaVerticalDivider />

      <DropDownMenu options={menuOptions.filter((e) => e)} />
    </div>
  );
};

export default UserActionIcons;
